import React from "react";
import { Button, theme } from "antd";
import { ArrowRightOutlined, ArrowLeftOutlined } from "@ant-design/icons";

const SOURCE_CREDITS = {
    unsplash: { label: "Unsplash", url: "https://unsplash.com" },
    pexels: { label: "Pexels", url: "https://www.pexels.com" },
    pixabay: { label: "Pixabay", url: "https://pixabay.com" },
    pixabay_png: { label: "Pixabay", url: "https://pixabay.com" },
    gif: { label: "Tenor", url: "https://tenor.com" },
};

function resolvePreviewSrc(image) {
    return (
        image.url ||
        image.src?.medium ||
        image.previewURL ||
        image.urls?.small ||
        image.media?.[0]?.gif?.url ||
        image.media?.[0]?.tinygif?.url ||
        image.src
    );
}

function Footer({
    isMobile,
    isTrash,
    activeSource,
    selectedImages,
    page,
    totalPages,
    isCollectionSelected,
    onClose,
    onProceed,
    onRestore,
    onDeleteSelected,
    onPermanentDelete,
    onPageChange,
}) {
    const { colorTextSecondary, colorLink, colorBorder } = theme.useToken();
    const activeCredit = SOURCE_CREDITS[activeSource];

    return (
        <div
            style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
                gap: isMobile ? 12 : 8,
                padding: isMobile ? "0px 0px 10px" : "12px 0px",
            }}
        >
            {/* DESKTOP ATTRIBUTION */}
            {!isMobile && (
                <div style={{ fontSize: 12, color: colorTextSecondary }}>
                    {activeCredit && !isTrash && (
                        <>
                            Powered by{" "}
                            <a
                                href={activeCredit.url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: colorLink }}
                            >
                                {activeCredit.label}
                            </a>
                        </>
                    )}
                </div>
            )}

            {/* MOBILE ATTRIBUTION */}
            {isMobile && activeCredit && !isTrash && (
                <div
                    style={{
                        fontSize: 11,
                        color: colorTextSecondary,
                        order: -2,
                    }}
                >
                    Powered by{" "}
                    <a
                        href={activeCredit.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: colorLink }}
                    >
                        {activeCredit.label}
                    </a>
                </div>
            )}

            {/* SELECTED IMAGE PREVIEWS */}
            {!isMobile && selectedImages.length > 0 && (
                <div
                    style={{
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                        display: "flex",
                        gap: 8,
                        padding: isMobile ? 0 : "0 16px",
                        flex: 1,
                        order: isMobile ? -1 : 0,
                    }}
                >
                    {selectedImages.map((image, index) => (
                        <img
                            key={index}
                            src={resolvePreviewSrc(image)}
                            alt="Selected preview"
                            style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: `1px solid ${colorBorder}`,
                                flexShrink: 0,
                            }}
                        />
                    ))}
                </div>
            )}

            {/* MOBILE PAGINATION */}
            {isMobile &&
                !isTrash &&
                activeSource !== "collection" &&
                totalPages > 1 && (
                    <div style={{ display: "flex", gap: 8, order: -2 }}>
                        <Button
                            type="default"
                            icon={<ArrowLeftOutlined />}
                            disabled={page === 1}
                            onClick={() => onPageChange(Math.max(1, page - 1))}
                            style={{ minHeight: 44 }}
                        />
                        <Button
                            type="default"
                            icon={<ArrowRightOutlined />}
                            disabled={page >= totalPages}
                            onClick={() => onPageChange(page + 1)}
                            style={{ minHeight: 44 }}
                        />
                    </div>
                )}

            {/* DESKTOP PAGINATION */}
            {!isMobile &&
                !isTrash &&
                activeSource !== "collection" &&
                totalPages > 1 && (
                    <div style={{ display: "flex", gap: 8 }}>
                        <Button
                            type="default"
                            shape="circle"
                            icon={<ArrowLeftOutlined />}
                            disabled={page === 1}
                            onClick={() => onPageChange(Math.max(1, page - 1))}
                        />
                        <Button
                            type="default"
                            shape="circle"
                            icon={<ArrowRightOutlined />}
                            disabled={page >= totalPages}
                            onClick={() => onPageChange(page + 1)}
                        />
                    </div>
                )}

            {/* ACTION BUTTONS */}
            <div
                style={{
                    display: "flex",
                    gap: 8,
                    width: isMobile ? "100%" : "auto",
                    justifyContent: isMobile ? "stretch" : "flex-end",
                    order: isMobile ? 1 : 0,
                }}
            >
                <Button
                    onClick={onClose}
                    style={{
                        minHeight: isMobile ? 44 : "auto",
                        flex: isMobile ? 1 : 0,
                    }}
                >
                    Cancel
                </Button>

                {isTrash ? (
                    <>
                        <Button
                            disabled={selectedImages.length === 0}
                            onClick={onRestore}
                            style={{
                                minHeight: isMobile ? 44 : "auto",
                                flex: isMobile ? 1 : 0,
                            }}
                        >
                            Restore
                        </Button>
                        <Button
                            danger
                            disabled={selectedImages.length === 0}
                            onClick={onPermanentDelete}
                            style={{
                                minHeight: isMobile ? 44 : "auto",
                                flex: isMobile ? 1 : 0,
                            }}
                        >
                            Delete
                        </Button>
                    </>
                ) : (
                    <>
                        {isCollectionSelected && (
                            <Button
                                danger
                                onClick={onDeleteSelected}
                                style={{
                                    minHeight: isMobile ? 44 : "auto",
                                    flex: isMobile ? 1 : 0,
                                }}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            type="primary"
                            disabled={selectedImages.length === 0}
                            onClick={onProceed}
                            style={{
                                minHeight: isMobile ? 44 : "auto",
                                flex: isMobile ? 1 : 0,
                            }}
                        >
                            Proceed
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

export default Footer;
